import { useEffect, useRef, useState } from "react";
import { customerService } from "../../services/customerService";
import Icon from "./Icon";

/**
 * Sunucu taraflı akıllı müşteri seçici (Autocomplete / Smart Search).
 * - Sayfa açıldığında veya kutuya tıklanınca rasgele müşteri listelemez.
 * - Yalnızca kullanıcı EN AZ 2 KARAKTER yazınca sunucuda hassas arama yapar.
 * - Çirkin görüntüyü ve kalabalığı önler, tıklamada sadece rehber notu gösterir.
 */
export default function AsyncCustomerSelect({
  value,
  initialLabel,
  onChange,
  placeholder = "Müşteri ara (en az 2 harf)…",
}) {
  const [query, setQuery] = useState("");
  const [label, setLabel] = useState(initialLabel || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);

  // Hızlı Ekleme Formu State
  const [showQuickAdd, setShowQuickAdd] = useState(false);
  const [newFullName, setNewFullName] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [addError, setAddError] = useState(null);

  const boxRef = useRef(null);

  useEffect(() => {
    setLabel(initialLabel || "");
  }, [initialLabel]);

  // Debounce'lu sunucu araması (Yalnızca en az 2 karakter yazıldığında tetiklenir)
  useEffect(() => {
    if (!open || showQuickAdd) return;
    const trimmed = query.trim();
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      return;
    }

    const t = setTimeout(() => {
      setLoading(true);
      customerService
        .list({ search: trimmed, size: 6, active: true })
        .then((r) => setResults(r.data?.data?.content ?? []))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, 250);

    return () => clearTimeout(t);
  }, [query, open, showQuickAdd]);

  // Dışarı tıklayınca kapat
  useEffect(() => {
    const h = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) {
        setOpen(false);
        setShowQuickAdd(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (c) => {
    setLabel(c.fullName);
    onChange(c.id, c);
    setOpen(false);
    setShowQuickAdd(false);
    setQuery("");
  };

  const clear = () => {
    setLabel("");
    onChange("", null, "");
    setResults([]);
    setQuery("");
    setShowQuickAdd(false);
  };

  // Hızlı Müşteri Oluşturma (Inline Form İle)
  const handleQuickCreate = async (e) => {
    if (e) e.preventDefault();
    const nameToCreate = newFullName.trim() || query.trim() || "Misafir Müşteri";

    try {
      setCreating(true);
      setAddError(null);
      const res = await customerService.create({
        fullName: nameToCreate,
        phone: newPhone.trim() || undefined,
      });

      const newCustomer = res.data?.data;
      if (newCustomer) {
        pick(newCustomer);
      }
    } catch (err) {
      setAddError(
        err.response?.data?.error ??
          err.message ??
          "Hızlı müşteri oluşturulamadı"
      );
    } finally {
      setCreating(false);
    }
  };

  const showChip = value && label && !open;
  const isQueryTooShort = query.trim().length < 2;

  return (
    <div className="relative" ref={boxRef}>
      {showChip ? (
        <div className="w-full flex items-center justify-between bg-white border border-outline-variant rounded-xl px-3 py-2.5 shadow-sm">
          <div className="flex items-center gap-2 truncate">
            <span className="w-2.5 h-2.5 rounded-full bg-emerald-500 shrink-0" />
            <span className="truncate font-semibold text-on-surface">
              {label}
            </span>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button
              type="button"
              onClick={() => setOpen(true)}
              title="Değiştir"
              className="p-1 text-on-surface-variant hover:text-primary transition rounded-lg hover:bg-surface-container-high cursor-pointer"
            >
              <Icon name="search" className="!text-[18px]" />
            </button>
            <button
              type="button"
              onClick={clear}
              title="Temizle"
              className="p-1 text-on-surface-variant hover:text-rose-600 transition rounded-lg hover:bg-rose-50 cursor-pointer"
            >
              <Icon name="close" className="!text-[18px]" />
            </button>
          </div>
        </div>
      ) : (
        <div className="relative flex items-center gap-2">
          <div className="relative flex-1">
            <input
              autoFocus={open}
              value={query}
              onChange={(e) => {
                const val = e.target.value;
                setQuery(val);
                if (onChange) onChange("", null, val);
              }}
              onFocus={() => setOpen(true)}
              placeholder={placeholder}
              className="w-full bg-white border border-outline-variant rounded-xl pl-9 pr-3 py-2.5 text-sm outline-none focus:border-primary transition-all"
            />
            <Icon
              name="search"
              className="absolute left-2.5 top-3 text-on-surface-variant !text-[18px]"
            />
          </div>

          <button
            type="button"
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => {
              setNewFullName(query.trim() || "");
              setNewPhone("");
              setShowQuickAdd(true);
              setOpen(true);
            }}
            className="shrink-0 px-3 py-2.5 bg-primary-100 hover:bg-primary-200 text-primary font-semibold text-xs rounded-xl flex items-center gap-1 transition cursor-pointer"
            title="Hızlı Müşteri Ekle"
          >
            <Icon name="person_add" className="!text-[16px]" />
            <span className="hidden sm:inline">Hızlı Ekle</span>
          </button>
        </div>
      )}

      {/* Dropdown Menu & Quick Add Box */}
      {open && (
        <div className="absolute z-50 mt-1 w-full bg-white border border-outline-variant rounded-2xl shadow-xl max-h-80 overflow-y-auto divide-y divide-outline-variant/30">
          {/* Option A: Quick Add Inline Form */}
          {showQuickAdd ? (
            <div className="p-4 space-y-3 bg-surface-container-low">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-on-surface flex items-center gap-1.5">
                  <Icon name="person_add" className="text-primary !text-[16px]" />
                  Hızlı Müşteri Oluştur
                </span>
                <button
                  type="button"
                  onClick={() => setShowQuickAdd(false)}
                  className="text-xs text-on-surface-variant hover:text-on-surface cursor-pointer"
                >
                  <Icon name="close" className="!text-[16px]" />
                </button>
              </div>

              {addError && (
                <p className="text-[11px] text-rose-600 bg-rose-50 p-2 rounded-lg border border-rose-200">
                  {addError}
                </p>
              )}

              <div>
                <label className="block text-[11px] font-semibold text-on-surface-variant mb-1">
                  Ad Soyad
                </label>
                <input
                  type="text"
                  placeholder="Müşteri Adı Soyadı"
                  value={newFullName}
                  onChange={(e) => setNewFullName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleQuickCreate();
                    }
                  }}
                  className="w-full px-3 py-2 bg-white border border-outline-variant rounded-lg text-xs outline-none focus:border-primary"
                />
              </div>

              <div>
                <label className="block text-[11px] font-semibold text-on-surface-variant mb-1">
                  Telefon (Opsiyonel)
                </label>
                <input
                  type="tel"
                  placeholder="05xx xxx xx xx"
                  value={newPhone}
                  onChange={(e) => setNewPhone(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleQuickCreate();
                    }
                  }}
                  className="w-full px-3 py-2 bg-white border border-outline-variant rounded-lg text-xs outline-none focus:border-primary"
                />
              </div>

              <div className="flex items-center gap-2 pt-1">
                <button
                  type="button"
                  onClick={handleQuickCreate}
                  disabled={creating}
                  className="flex-1 py-2 bg-primary hover:bg-primary-600 text-white text-xs font-semibold rounded-lg shadow-sm transition disabled:opacity-50 flex items-center justify-center gap-1 cursor-pointer"
                >
                  {creating ? (
                    <>
                      <Icon name="sync" className="animate-spin !text-[14px]" />
                      <span>Kaydediliyor…</span>
                    </>
                  ) : (
                    <span>Kaydet & Randevuya Atan</span>
                  )}
                </button>
              </div>
            </div>
          ) : (
            <>
              {/* Option B: Search Guidance when query < 2 chars */}
              {isQueryTooShort && (
                <div className="p-3 text-xs text-on-surface-variant/80 flex items-center gap-2 bg-surface-container-low/40">
                  <Icon name="search" className="text-primary/60 !text-[16px]" />
                  <span>Arama yapmak için en az <strong>2 karakter</strong> yazın (Örn: Ayş)</span>
                </div>
              )}

              {/* Option C: Loading Indicator */}
              {!isQueryTooShort && loading && (
                <div className="p-3 text-xs text-on-surface-variant flex items-center gap-2">
                  <Icon name="sync" className="animate-spin text-primary !text-[16px]" />
                  <span>Aranıyor…</span>
                </div>
              )}

              {/* Option D: Exact Search Results */}
              {!isQueryTooShort && !loading && results.length > 0 && (
                <div className="py-1">
                  <div className="px-3 py-1 text-[10px] font-bold text-on-surface-variant/60 uppercase tracking-wider">
                    Eşleşen Müşteriler ({results.length})
                  </div>
                  {results.map((c) => (
                    <button
                      type="button"
                      key={c.id}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => pick(c)}
                      className="w-full text-left px-3 py-2 hover:bg-surface-container-low flex items-center justify-between transition cursor-pointer"
                    >
                      <span className="truncate font-medium text-xs text-on-surface">
                        {c.fullName}
                      </span>
                      <span className="text-[11px] text-on-surface-variant ml-2 shrink-0 font-mono">
                        {c.phone || "—"}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Option E: No Results Found */}
              {!isQueryTooShort && !loading && results.length === 0 && (
                <div className="p-3 text-xs text-on-surface-variant italic">
                  "{query.trim()}" için kayıtlı müşteri bulunamadı. Doğrudan <strong>"Kaydet/Güncelle"</strong> butonuna basabilir veya sağdaki <strong>"+ Hızlı Ekle"</strong> butonunu kullanabilirsiniz.
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
